import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import clsx from 'clsx';
import MonitoringSchedulesTab from 'components/MonitoringAgent/MonitoringSchedulesTab/MonitoringSchedulesTab';
import MonitoringSessionsTab from 'components/MonitoringAgent/MonitoringSessionsTab/MonitoringSessionsTab';
import SidePanel from 'components/SidePanel/SidePanel';

import type { TMonitoringHubTab } from 'constants/interfaces/IMonitoringHubPanelState';
import type IMonitoringAgentHubSidebar from './interfaces/IMonitoringAgentHubSidebar';

import styles from './sass/MonitoringAgentHubSidebar.module.scss';

function MonitoringAgentHubSidebar({
  open,
  titleKey,
  agentDefinition,
  sessions,
  schedules,
  activeTab,
  allAvailableCards,
  valueChainMapId,
  isSessionsLoading = false,
  isSessionsError = false,
  isSchedulesLoading = false,
  isSchedulesError = false,
  isScheduleActionLoading = false,
  isSessionActionLoading = false,
  onBack,
  onClose,
  onActiveTabChange,
  onNewSelection,
  onNewSchedule,
  onOpenSchedule,
  onEditSchedule,
  onDeleteSchedule,
  onPauseSchedule,
  onResumeSchedule,
  onEditSession,
  onDeleteSession,
  onViewSessionResults,
}: IMonitoringAgentHubSidebar): React.JSX.Element {
  const { t } = useTranslation();

  const handleTabChange = (tab: TMonitoringHubTab) => {
    onActiveTabChange(tab);
  };

  return (
    <SidePanel
      open={open}
      className={styles.container}
      ariaModal
      role="dialog"
      ariaLabel={t(titleKey)}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onBack}
          aria-label={t('common.back')}
          data-i18n-key="common.back"
        >
          <ArrowLeftIcon aria-hidden="true" />
        </button>
        <span className={styles.headerTitle}>{t(titleKey)}</span>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onClose}
          aria-label={t('common.close')}
          data-i18n-key="common.close"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.tabs} role="tablist" aria-label={t('valueChainMap.monitoringHubTabsLabel')}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'sessions'}
            className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'sessions' })}
            onClick={() => handleTabChange('sessions')}
          >
            {t('valueChainMap.monitoringHubSessionsTab')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'schedules'}
            className={clsx(styles.tab, { [styles.tabActive]: activeTab === 'schedules' })}
            onClick={() => handleTabChange('schedules')}
          >
            {t('valueChainMap.monitoringHubSchedulesTab')}
          </button>
        </div>

        {activeTab === 'sessions' ? (
          <MonitoringSessionsTab
            sessions={sessions}
            agentDefinition={agentDefinition}
            allAvailableCards={allAvailableCards}
            isLoading={isSessionsLoading}
            isError={isSessionsError}
            isActionLoading={isSessionActionLoading}
            onNewSelection={onNewSelection}
            onEditSession={onEditSession}
            onDeleteSession={onDeleteSession}
            onViewSessionResults={onViewSessionResults}
          />
        ) : (
          <MonitoringSchedulesTab
            schedules={schedules}
            sessionType={agentDefinition.sessionType}
            selectionMode={agentDefinition.selectionMode}
            schedulesInfoKey={agentDefinition.schedulesInfoKey}
            valueChainMapId={valueChainMapId}
            isLoading={isSchedulesLoading}
            isError={isSchedulesError}
            isActionLoading={isScheduleActionLoading}
            onNewSchedule={onNewSchedule}
            onOpenSchedule={onOpenSchedule}
            onEditSchedule={onEditSchedule}
            onDeleteSchedule={onDeleteSchedule}
            onPauseSchedule={onPauseSchedule}
            onResumeSchedule={onResumeSchedule}
          />
        )}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
        >
          {t('common.close')}
        </button>
      </div>
    </SidePanel>
  );
}

export default MonitoringAgentHubSidebar;
